import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import ConvertToAnnotationCommand from './convert-to-annotation';
import ResizeImageCommand from '@ckeditor/ckeditor5-image/src/imageresize/resizeimagecommand';
import ExportWordCommand from '@ckeditor/ckeditor5-export-word/src/exportwordcommand';
import { viewToModelPositionOutsideModelElement } from '@ckeditor/ckeditor5-widget/src/utils';
import clipboardInputHandler from './clipboard-input-handler';
import {
  makeAnnotationElementStructure,
  getAnnotationDataFromView,
  createAnnotationView,
  makeAnnotationWidget,
  annotationOuterElement,
} from './annotation-element-structure';
import ClickObserver from '@ckeditor/ckeditor5-engine/src/view/observer/clickobserver';
import DoubleClickObserver from './doubleclickobserver';
import insertAnchorCommand from './insert-anchor';

class AnnotationEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    this._defineSchema();
    this._defineConverters();
    this._defineClipboardInputOutput();
    this._defineEventListeners();

    // View-to-model position mapping is needed because an annotation element in the model is represented by a single element,
    // but in the view it is a more complex structure.
    this.editor.editing.mapper.on(
      'viewToModelPosition',
      viewToModelPositionOutsideModelElement(this.editor.model, (viewElement) =>
        viewElement.hasClass('annotation'),
      ),
    );

    this.editor.commands.add('convertToAnnotation', new ConvertToAnnotationCommand(this.editor));
    this.editor.commands.add('resizeImage', new ResizeImageCommand(this.editor));
    this.editor.commands.add('exportWord', new ExportWordCommand(this.editor));
    this.editor.commands.add('insertAnchor', new insertAnchorCommand(this.editor));

    // allow convert to annotation while tracking changes
    if (this.editor.plugins.has('TrackChangesEditing')) {
      const trackChangesEditing = this.editor.plugins.get('TrackChangesEditing');
      trackChangesEditing.enableCommand('convertToAnnotation');
      trackChangesEditing.enableCommand('insertAnchor');
    }

    // open comments panel for ckeditor modal
    const commentCommand = this.editor.commands.get('addCommentThread');
    if (commentCommand) {
      commentCommand.on('execute', () => {
        const event = new CustomEvent('ckeditor-add-comment-thread', {
          bubbles: true,
        });
        window.dispatchEvent(event);
      });
    }

    const onClipboardInputResponse = (event) => {
      const { editor, evt, data, viewDocument, dependencies } = event.detail;
      const isInstanceEqual = editor.id === this.editor.id;
      if (!isInstanceEqual) return;
      clipboardInputHandler(evt, data, viewDocument, dependencies);
    };

    this.editor.on('ready', () => {
      window.addEventListener('ckeditor-clipboard-input-response', onClipboardInputResponse);
    });
    this.editor.on('destroy', () => {
      window.removeEventListener('ckeditor-clipboard-input-response', onClipboardInputResponse);
    });
  }

  // define the schema of our annotation element
  _defineSchema() {
    this.editor.model.schema.register('annotation', {
      allowWhere: '$text',
      isInline: true,
      isObject: true,
      allowAttributes: ['itemId', 'displayLabel', 'itemFieldType', 'style', 'width', 'isMultiple'],
    });
  }

  _defineConverters() {
    const conversion = this.editor.conversion;
    const { domConverter } = this.editor.editing.view;

    // Data-to-model conversion.
    // Any time data is loaded into editor
    conversion.for('upcast').elementToElement({
      view: {
        name: annotationOuterElement,
        classes: ['annotation'],
      },
      model: (viewElement, { writer }) =>
        writer.createElement('annotation', getAnnotationDataFromView(viewElement, domConverter)),
    });

    // Model-to-data conversion.
    conversion.for('dataDowncast').elementToElement({
      model: 'annotation',
      view: (modelItem, { writer: viewWriter }) => createAnnotationView(modelItem, viewWriter),
    });

    // Model-to-view conversion. (annotation drop)
    conversion.for('editingDowncast').elementToElement({
      model: 'annotation',
      view: (modelItem, { writer: viewWriter }) =>
        makeAnnotationWidget(this.editor, modelItem, viewWriter),
    });

    // on model width attribute change
    conversion.for('editingDowncast').add((dispatcher) => {
      dispatcher.on('attribute:width:annotation', (evt, data, conversionApi) => {
        if (!data.attributeOldValue || !data.attributeNewValue) {
          return;
        }
        const viewWriter = conversionApi.writer;
        const element = conversionApi.mapper.toViewElement(data.item);
        const { outer } = makeAnnotationElementStructure({ width: data.attributeNewValue });
        viewWriter.setAttribute('style', outer.attributes.style, element);
      });
    });

    // on model display text change
    conversion.for('editingDowncast').add((dispatcher) => {
      dispatcher.on('attribute:displayLabel:annotation', (evt, data, conversionApi) => {
        // Update annotation display text by removing old annotation
        // and inserting new one with updated text at same position.
        if (!data.attributeOldValue || !data.attributeNewValue) return;

        const viewWriter = conversionApi.writer;
        const viewElement = conversionApi.mapper.toViewElement(data.item);
        const modelItem = conversionApi.mapper.toModelElement(viewElement);
        const newDisplayText = data.attributeNewValue;

        // update display text on model item
        viewWriter.setAttribute('displayLabel', newDisplayText, modelItem);

        // get position of annotation
        const viewPosition = conversionApi.mapper.toViewPosition(data.range.start);

        // remove old annotation
        viewWriter.remove(viewWriter.createRangeOn(viewElement));

        // make new annotation and bind to model item
        const newAnnotation = makeAnnotationWidget(this.editor, modelItem, viewWriter);
        conversionApi.mapper.bindElements(modelItem, newAnnotation);

        // insert at position
        conversionApi.writer.insert(viewPosition, newAnnotation);
      });
    });

    // on model style attribute change
    conversion.for('editingDowncast').add((dispatcher) => {
      dispatcher.on('attribute:style:annotation', (evt, data, conversionApi) => {
        if (!data.attributeOldValue || !data.attributeNewValue) return;
        const viewWriter = conversionApi.writer;
        const viewElement = conversionApi.mapper.toViewElement(data.item);
        const newStyle = data.attributeNewValue;
        viewWriter.setAttribute('style', newStyle, viewElement);
      });
    });
  }

  // Integration with the clipboard pipeline.
  _defineClipboardInputOutput() {
    const editor = this.editor;
    const view = editor.editing.view;
    const viewDocument = view.document;

    // Processing pasted or dropped content.
    this.listenTo(viewDocument, 'clipboardInput', (evt, data) => {
      const event = new CustomEvent('ckeditor-clipboard-input', {
        bubbles: true,
        detail: {
          editor,
          evt,
          data,
          viewDocument,
        },
      });
      window.dispatchEvent(event);
    });

    // Processing copied, pasted or dragged content.
    this.listenTo(document, 'clipboardOutput', (evt, data) => {
      if (data.content.childCount != 1) return;
      const viewElement = data.content.getChild(0);
      // if ( viewElement.is( 'element', 'div' ) && viewElement.hasClass( 'annotation' ) ) {
      // 	data.dataTransfer.setData( 'contact', JSON.stringify( getAnnotationDataFromView( viewElement ) ) );
      // }
    });
  }

  // add event listener for annotation click
  _defineEventListeners() {
    const { editor } = this;
    const view = editor.editing.view;
    const viewDocument = view.document;

    view.addObserver(ClickObserver);
    view.addObserver(DoubleClickObserver);

    const dispatchEvent = (name, detail) => {
      const event = new CustomEvent(name, { bubbles: true, detail });
      window.dispatchEvent(event);
    };

    const clickListener = (event, data) => {
      const { target } = data;
      if (!target) return;

      if (target.name === 'a' && target.hasClass('hrbr-highlight')) {
        console.log('hrbr-highlight clicked', target.getAttribute('data-anchorid'));
        return;
      }
      const modelElement = editor.editing.mapper.toModelElement(target);
      if (modelElement?.name === 'annotation') {
        const itemId = modelElement.getAttribute('itemId');
        const content = modelElement.getAttribute('displayLabel');
        dispatchEvent(`ckeditor-annotation-click`, { itemId, content, eventName: event.name });
      }
    };

    editor.listenTo(viewDocument, 'click', clickListener);
    editor.listenTo(viewDocument, 'dblclick', clickListener);
  }
}

export default AnnotationEditing;
